import { useEffect, useRef, useState } from 'react';
import { NavArrowDown } from 'iconoir-react';
import { Collapsible, CollapsibleTrigger } from 'components/ui/collapsible';
import type { StreamStatusEntry } from '../types/chat';
import { StreamStatusTimeline } from './StreamStatusTimeline';
import { cn } from 'lib/utils';

interface StreamActivityDropdownProps {
  entries: StreamStatusEntry[];
  label?: string;
  /** Inline Steps stay open so new statuses are visible as they arrive. */
  defaultOpen?: boolean;
  /** Auto-open while a tool/skill call is still in progress (click can still close). */
  forceOpen?: boolean;
  /** When false, auto-collapse; user can still reopen via click. */
  active?: boolean;
  shimmerActiveRow?: boolean;
}

const PANEL_TRANSITION =
  'transition-[grid-template-rows,opacity,margin] duration-500 ease-in-out motion-reduce:transition-none';

export function StreamActivityDropdown({
  entries,
  label = 'Steps',
  defaultOpen = false,
  forceOpen = false,
  active = true,
  shimmerActiveRow = false,
}: Readonly<StreamActivityDropdownProps>) {
  const [isOpen, setIsOpen] = useState(defaultOpen || forceOpen);
  const prevForceOpen = useRef(forceOpen);
  const prevActive = useRef(active);
  const visibleEntries = entries.filter((entry) => entry.icon !== 'processing_started');

  useEffect(() => {
    const wasActive = prevActive.current;
    const wasForceOpen = prevForceOpen.current;
    prevActive.current = active;
    prevForceOpen.current = forceOpen;

    // Auto-close when this Steps block finished.
    if (!active && !forceOpen) {
      setIsOpen(false);
      return;
    }

    // Auto-open only on transitions — never lock the panel against user clicks.
    const becameForceOpen = !wasForceOpen && forceOpen;
    const becameActive = !wasActive && active;
    if (becameForceOpen || (becameActive && (defaultOpen || forceOpen))) {
      setIsOpen(true);
    }
  }, [active, defaultOpen, forceOpen]);

  if (!visibleEntries.length) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group inline-flex items-center gap-1 rounded-md py-0.5 text-left transition-colors hover:bg-muted/60"
          aria-expanded={isOpen}
        >
          <span className="text-sm font-medium text-[#6D6D6D] dark:text-muted-foreground">
            {label}
          </span>
          <NavArrowDown
            width={14}
            height={14}
            strokeWidth={2}
            className={cn(
              'shrink-0 text-[#9C9C9E] transition-transform duration-500 ease-in-out motion-reduce:transition-none dark:text-muted-foreground',
              isOpen && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </CollapsibleTrigger>

      <div
        aria-hidden={!isOpen}
        className={cn(
          'grid overflow-hidden',
          PANEL_TRANSITION,
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="pt-2 pl-0.5">
            <StreamStatusTimeline
              entries={visibleEntries}
              shimmerActiveRow={shimmerActiveRow}
              staggerReveal={isOpen}
              className="[&_.stream-status-timeline-item]:pb-3"
            />
          </div>
        </div>
      </div>
    </Collapsible>
  );
}
